import React, { useEffect, useState } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    RootRef,
    useMediaQuery,
    useTheme
} from "@material-ui/core";
import { observer } from "mobx-react";
import { useStores } from "../../../../../hooks";
import { BookingFormType } from "../../../store/booking/forms";
import { localize } from "../../../../../common/libs";
import { MenuBookingItem } from "../../../../../services/menu/models";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { AlignJustify as DragIcon,  } from "react-feather";
import { Typography } from "../../../../../common/components/libs";
import { SnackbarType } from "../../../../../stores/common";
import {Trans, useTranslation} from "react-i18next";

const getItemStyle = (isDragging, draggableStyle) => ({
    // styles we need to apply on draggables
    ...draggableStyle,
    padding: "10px 0px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    margin: "10px 5px",
    borderRadius: 5,

    ...(isDragging && {
        padding: "10px",
        background: "rgba(25, 118, 210, 0.1)"
    })
});

const EditMenuItemsOrdersForm = () => {
    const theme = useTheme();
    const isSm = useMediaQuery(theme.breakpoints.up('sm'));
    const [items, setItems] = useState<MenuBookingItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const { t} = useTranslation(["general", "menu"]);



    const { bookingStore, generalStore } = useStores();
    const { forms } = bookingStore;
    const { open } = forms.forms[BookingFormType.editItemsOrdersForm];

    useEffect(() => {
        if(!bookingStore.currentMenu) return;
        const currentCategory = bookingStore.currentMenu.menuCategories[bookingStore.selectedCategory];
        if(!currentCategory) return;
        setItems(bookingStore.currentMenu.menuItemsByCategoryId[currentCategory.id] || []);
    }, [bookingStore.currentMenu, bookingStore.selectedCategory, open]);


    const closeForm = () => {
        forms.closeForm(BookingFormType.editItemsOrdersForm)
    };

    const handleSubmit = async () => {
        setIsLoading(true);
        for(let item of items){
            await bookingStore.updateBookingItem(item.id, {
                ...item.data,
                sortOrder: item.data.sortOrder
            }, false);
        }
        await bookingStore.reloadMenu();
        generalStore.showSnackbar({
            title: t("menu:menuEditor.itemOrderChanged"),
            type: SnackbarType.success
        })
        setIsLoading(false);
        closeForm();
    };

    const reorder = (categories: MenuBookingItem[], startIndex: number, endIndex: number) => {
        const result = [...categories];
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result.map((item, index) => {
            return {
                ...item,
                data: {
                    ...item.data,
                    sortOrder: index
                }
            }
        });
    };

    const onDragEnd = (result: DropResult) => {
        if (!result.destination) return;
        const newItems = reorder(items, result.source.index, result.destination.index);
        setItems(newItems);
    }


    return (
        <Dialog
            open={open}
            onClose={closeForm}
            aria-labelledby="form-dialog-title"
            fullWidth={true}
            maxWidth={"sm"}
            fullScreen={!isSm}
        >
            <DialogTitle id="form-dialog-title">
                { t("menu:menuEditor.editingPositions")}
            </DialogTitle>
            <DialogContent>
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="droppable">
                        {(provided) => {
                            return <RootRef rootRef={provided.innerRef}>
                                <List>
                                    {
                                        items.map((category, index) => {
                                            return <Draggable key={category.id} draggableId={category.id.toString()}
                                                              index={index}>
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        style={getItemStyle(
                                                            snapshot.isDragging,
                                                            provided.draggableProps.style
                                                        )}
                                                    >
                                                        <div style={{display: "flex"}}>
                                                            <DragIcon size={20} color="#b5b5b5"/>
                                                            <Typography variant='h6' marginLeft={4}>
                                                                {localize(category.data.name)}
                                                            </Typography>
                                                        </div>
                                                    </div>
                                                )}
                                            </Draggable>
                                        })

                                    }
                                    {provided.placeholder}
                                </List>
                            </RootRef>
                        }}
                    </Droppable>
                </DragDropContext>
            </DialogContent>

            <DialogActions>
                <Button onClick={closeForm}
                        color="primary"
                        disabled={isLoading}
                        variant={'outlined'}
                >
                    {t("general:cancel")}
                </Button>
                <Button
                    color="primary"
                    onClick={handleSubmit}
                    disabled={isLoading}
                    variant={'contained'}
                >
                    {t("general:save")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default observer(EditMenuItemsOrdersForm);
